export type WebhookAuthModeView = 'hmac-sha256' | 'bearer' | 'none';
export type WebhookBatchStatusView = 'open' | 'dispatching' | 'delivered' | 'failed';

export interface WebhookForm {
  readonly name: string;
  readonly targetRoleId: string;
  readonly authMode: WebhookAuthModeView;
  readonly enabled: boolean;
  readonly signatureHeader: string;
  readonly deliveryIdHeader: string;
  readonly rateLimitCount: number;
  readonly rateLimitWindowSeconds: number;
  readonly debounceSeconds: number;
  readonly maxPayloadBytes: number;
  readonly maxBatchEvents: number;
  readonly messageTemplate: string;
}

/** Prevent a query result from the previous team surviving one switch render. */
export function visibleWebhooksForTeam<T extends { readonly teamId: string }>(
  teamId: string,
  endpoints: readonly T[],
): readonly T[] {
  return endpoints.filter((endpoint) => endpoint.teamId === teamId);
}

export function blankWebhookForm(defaultRole = ''): WebhookForm {
  return {
    name: '',
    targetRoleId: defaultRole,
    authMode: 'hmac-sha256',
    enabled: true,
    signatureHeader: 'x-autonomous-team-signature',
    deliveryIdHeader: 'x-autonomous-team-delivery',
    rateLimitCount: 10,
    rateLimitWindowSeconds: 60,
    debounceSeconds: 60,
    maxPayloadBytes: 262_144,
    maxBatchEvents: 200,
    messageTemplate: 'Review the current state of the external system and process actionable items.',
  };
}

export function validateWebhookForm(form: WebhookForm): { readonly errors: readonly string[]; readonly warning: string | null } {
  const errors: string[] = [];
  if (form.name.trim().length === 0) errors.push('Name is required.');
  if (form.targetRoleId.trim().length === 0) errors.push('Choose an agent.');
  if (!Number.isInteger(form.debounceSeconds) || form.debounceSeconds < 1 || form.debounceSeconds > 3_600) {
    errors.push('Debounce must be between 1 and 3600 seconds.');
  }
  if (!Number.isInteger(form.rateLimitCount) || form.rateLimitCount < 1 || form.rateLimitCount > 10_000) {
    errors.push('Request limit must be between 1 and 10000.');
  }
  if (!Number.isInteger(form.rateLimitWindowSeconds) || form.rateLimitWindowSeconds < 1 || form.rateLimitWindowSeconds > 3_600) {
    errors.push('Rate-limit window must be between 1 and 3600 seconds.');
  }
  return {
    errors,
    warning:
      form.authMode === 'none'
        ? 'Anyone who obtains this URL can submit events. Use HMAC unless the sender cannot sign requests.'
        : null,
  };
}

export function batchTone(status: WebhookBatchStatusView): 'active' | 'danger' | 'wait' | 'info' {
  if (status === 'delivered') return 'active';
  if (status === 'failed') return 'danger';
  if (status === 'dispatching') return 'info';
  return 'wait';
}
